import React, { createContext, useContext, useEffect, useState } from 'react';
import AllProducts from './data';
import { gtmTracking } from './helpers';
const CLASS_FULL_SCREEN = 'tos-full-screen';

const AppContext = createContext({
	page: '',
	prevPage: '',
	isMobile: '',
	isTablet: '',
	products: [],
	singleProduct: {},
	isSafari: '',
	isFullScreen: '',
	changePageHandler: () => {},
	productClickHandler: () => {},
	nextProductHandler: () => {},
	prevProductHandler: () => {},
	fullScreenHandler: () => {},
});

const AppProvider = ({ children }) => {
	const [page, setPage] = useState('home_page');
	const [prevPage, setPrevPage] = useState(null);
	const products = AllProducts;
	const [singleProduct, setSingleProduct] = useState();
	const [width, setWidth] = useState(window.innerWidth);
	const isMobile = width <= 800;
	const isTablet = width <= 999;
	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	const [isFullScreen, setIsFullScreen] = useState(false);

	useEffect(() => {
		window.addEventListener('fullscreenchange', () => {
			const isSetFullScreen =
				document.fullScreen ||
				document.mozFullScreen ||
				document.webkitIsFullScreen;
			setIsFullScreen(isSetFullScreen);
		});
	}, [isFullScreen]);

	const openFullScreen = () => {
		if (document.documentElement.requestFullscreen) {
			document.documentElement.requestFullscreen();
		} else if (document.documentElement.msRequestFullscreen) {
			document.documentElement.msRequestFullscreen();
		} else if (document.documentElement.mozRequestFullScreen) {
			document.documentElement.mozRequestFullScreen();
		} else if (document.documentElement.webkitRequestFullscreen) {
			document.documentElement.webkitRequestFullscreen(
				Element.ALLOW_KEYBOARD_INPUT,
			);
		}
		setIsFullScreen(true);
	};

	const fullScreenHandler = (forceFullScreen = false) => {
		if (forceFullScreen) {
			openFullScreen();
			return setIsFullScreen(true);
		} else {
			if (
				!document.fullscreenElement &&
				!document.mozFullScreenElement &&
				!document.webkitFullscreenElement &&
				!document.msFullscreenElement
			) {
				openFullScreen();
			} else {
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				} else if (document.mozCancelFullScreen) {
					document.mozCancelFullScreen();
				} else if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
				}
				setIsFullScreen(false);
			}
		}
	};

	useEffect(() => {
		if (isFullScreen) {
			document.body.classList.add(CLASS_FULL_SCREEN);
		} else {
			document.body.classList.remove(CLASS_FULL_SCREEN);
		}
	}, [isFullScreen]);

	function handleWindowSizeChange() {
		setWidth(window.innerWidth);
	}

	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		};
	}, []);

	const changePageHandler = page => {
		setPage(prevState => {
			setPrevPage(prevState);
			return page;
		});
		window.scrollTo(0, 0);
	};

	const productClickHandler = productName => {
		const matchingProduct = products.find(
			product => product.name === productName,
		);
		if (matchingProduct) {
			setSingleProduct(matchingProduct);
			changePageHandler('element_page');
		}
	};

	const nextProductHandler = () => {
		const matchingProduct = products.findIndex(
			product => product === singleProduct,
		);
		setPrevPage('element_page');
		if (matchingProduct + 1 === products.length) {
			setSingleProduct(products[0]);
			gtmTracking('component list', products[0].name);
		} else {
			setSingleProduct(products[matchingProduct + 1]);
			gtmTracking('component list', products[matchingProduct + 1].name);
		}
	};

	const prevProductHandler = () => {
		const matchingProduct = products.findIndex(
			product => product === singleProduct,
		);
		setPrevPage('element_page');
		if (matchingProduct - 1 < 0) {
			setSingleProduct(products[products.length - 1]);
			gtmTracking('component list', products[products.length - 1].name);
		} else {
			setSingleProduct(products[matchingProduct - 1]);
			gtmTracking('component list', products[matchingProduct - 1].name);
		}
	};

	const context = {
		page,
		prevPage,
		isMobile,
		isTablet,
		products,
		singleProduct,
		isSafari,
		isFullScreen,
		changePageHandler,
		productClickHandler,
		nextProductHandler,
		prevProductHandler,
		fullScreenHandler,
	};

	return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
};

export const useGlobalContext = () => {
	return useContext(AppContext);
};

export { AppContext, AppProvider };
